import type { ZxingEngine } from "../engine/ZxingEngine.js";
import { analyzeFrame, enhanceForCondition, lumaToImageData, toLuma } from "../enhance/index.js";
import type { EnhancementMode, FrameQuality, ScanResult } from "../types.js";

export interface DecodeAttempt {
  results: Omit<ScanResult, "enhanced" | "timestamp">[];
  enhanced: boolean;
  quality: FrameQuality;
}

/**
 * Shared "try raw, analyze, retry enhanced" logic used both by the live
 * camera pipeline (per frame) and by the one-off `scanImage` API (a single
 * uploaded photo) — so a low-light/glare photo picked from disk benefits
 * from the same recovery path a live scan does, not just a curated demo.
 */
export async function decodeWithEnhancement(
  engine: ZxingEngine,
  imageData: ImageData,
  enabledFormatIds: string[],
  enhancement: EnhancementMode,
): Promise<DecodeAttempt> {
  const luma = toLuma(imageData);
  const quality = analyzeFrame(luma, imageData.width, imageData.height);

  let results = await engine.decode(imageData, enabledFormatIds);
  let enhanced = false;

  if (results.length === 0 && enhancement !== "off" && quality.condition !== "good") {
    const processedLuma = enhanceForCondition(
      luma,
      imageData.width,
      imageData.height,
      quality,
      enhancement === "aggressive",
    );
    const enhancedImageData = lumaToImageData(processedLuma, imageData.width, imageData.height);
    // Processed pixels can manufacture edge structure that isn't really
    // there (see medianDenoise3x3's doc comment); requiring more consistent
    // scan lines on this retry trades a little recall for a lot less risk
    // of a linear-code false positive on a synthetic-looking frame.
    results = await engine.decode(enhancedImageData, enabledFormatIds, { minLineCount: 3 });
    enhanced = results.length > 0;
  }

  return { results, enhanced, quality };
}
